import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../config";
import {
  ArrowLeft, ArrowRight, Check, RefreshCw, Eye, EyeOff, ChevronDown,
  Mail, Smartphone, ShieldCheck, IdCard, Server, Cloud, AppWindow,
  CreditCard, FileText,
} from "lucide-react";
import "./signdoc.css";

const steps = [
  "Upload",
  "Configure Digital Identity",
  "Choose Mode",
  "Advanced Options",
  "Customize Signature",
  "Review & Sign",
];

const EMAIL_OTP_SEND_URL = () => `${API_URL}/api/otp/email/send`;
const EMAIL_OTP_VERIFY_URL = () => `${API_URL}/api/otp/email/verify`;
const ID_VERIFICATION_URL = () => `${API_URL}/api/identity/verify-id`;
const REVOKE_DIGITAL_ID_URL = () => `${API_URL}/api/digital-id/revoke`;
const RENEW_DIGITAL_ID_URL = () => `${API_URL}/api/digital-id/renew`;

// Cards that skip the "Device Details" step entirely and navigate straight
// to Choose Mode when clicked. Only "create" behaves this way today, but
// keeping it as a list keeps the behaviour easy to extend later.
const SKIP_DEVICE_DETAILS_KEYS = ["create"];

const CARD_DEFS = [
  {
    key: "device",
    title: "Device Use a Signature Creation Device",
    description: "Configure a smart card or token connected to your computer",
    icon: Smartphone,
  },
  {
    key: "file",
    title: "Use a Digital ID from a File",
    description: "Import an existing Digital ID that you have obtained as a file",
    icon: FileText,
  },
  {
    key: "create",
    title: "Create a New Digital ID",
    description: "Create your self-signed Digital ID",
    icon: IdCard,
  },
  {
    key: "hsm",
    title: "Hardware Security Module (HSM)",
    description: "Use a certificate stored in an HSM appliance",
    icon: Server,
  },
  {
    key: "cloud",
    title: "Cloud Signing Service",
    description: "Use a certificate from your cloud signing service",
    icon: Cloud,
  },
  {
    key: "windows",
    title: "Windows Certificate Store",
    description: "Use a certificate from Windows Certificate Store",
    icon: AppWindow,
  },
  {
    key: "pkcs11",
    title: "PKCS#11 Token (Advanced)",
    description: "Use any PKCS#11 compatible token or smart card",
    icon: CreditCard,
  },
  {
    key: "mobile",
    title: "Mobile / Remote Signing",
    description: "Sign using mobile device or remote signing service",
    icon: Smartphone,
  },
  {
    key: "csr",
    title: "Create CSR / Get Certificate",
    description: "Generate CSR and obtain certificate from CA",
    icon: FileText,
  },
  {
    key: "emailOtp",
    title: "Email OTP",
    description: "Verify using a one-time code sent to your registered email",
    icon: Mail,
  },
  {
    key: "idVerification",
    title: "ID Verification",
    description: "Verify your identity using a government-issued ID",
    icon: IdCard,
  },
  {
    key: "existing",
    title: "Use Existing Digital ID",
    description: "Select one of your previously created Digital IDs",
    icon: IdCard,
  },
];

export default function SelectIdentity() {
  const navigate = useNavigate();
  const location = useLocation();
  const uploadedFiles = location.state?.uploadedFiles || [];
  const currentStep = 1;
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const [digitalIds, setDigitalIds] = useState([]);
  const [selectedDigitalId, setSelectedDigitalId] = useState(null);
  const [loadingDigitalIds, setLoadingDigitalIds] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, []);

  const [selectedKey, setSelectedKey] = useState(null);
  const [statusByTitle, setStatusByTitle] = useState({});

  useEffect(() => {
    const fetchIdentities = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (!storedUser?.id) return;

        const res = await axios.get(
          `${API_URL}/api/user/identity-status/${storedUser.id}`
        );

        const identities = res?.data?.identities || [];
        const map = {};
        identities.forEach((item) => {
          map[item.title] = item;
        });
        setStatusByTitle(map);
      } catch (err) {
        console.error("Identity fetch error:", err);
      }
    };

    fetchIdentities();
  }, []);

  const [fields, setFields] = useState({});
  const [showPin, setShowPin] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionOk, setConnectionOk] = useState(false);

  // OTP flow state
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  // ID verification flow state
  const [idLoading, setIdLoading] = useState(false);
  const [idError, setIdError] = useState("");
  const [idVerified, setIdVerified] = useState(false);

  useEffect(() => {
    setFields({ email: user?.email || "" });
    setShowPin(false);
    setTestingConnection(false);
    setConnectionOk(false);
    setOtpSent(false);
    setOtpError("");
    setOtpVerified(false);
    setIdError("");
    setIdVerified(false);
  }, [selectedKey]);

  useEffect(() => {
    if (selectedKey !== "existing") return;

    const fetchDigitalIds = async () => {
      try {
        setLoadingDigitalIds(true);

        if (!user?.id) {
          console.error("User ID missing");
          return;
        }

        const res = await axios.get(`${API_URL}/api/digital-id/list`, {
          params: { userId: user.id },
        });

        setDigitalIds(res.data.identities || []);
      } catch (err) {
        console.error("Digital ID API Error:", err);
        alert(err.response?.data?.error || "Unable to load Digital IDs");
      } finally {
        setLoadingDigitalIds(false);
      }
    };

    fetchDigitalIds();
  }, [selectedKey, user.id]);

  const handleRevokeDigitalId = async () => {
    if (!selectedDigitalId) {
      alert("Please select a Digital ID.");
      return;
    }

    const confirmRevoke = window.confirm(
      "Are you sure you want to revoke this Digital ID?"
    );

    if (!confirmRevoke) return;

    try {
      await axios.post(REVOKE_DIGITAL_ID_URL(), {
        digitalId: selectedDigitalId.id,
        userId: user.id,
      });

      alert("Digital ID revoked successfully.");

      const updated = { ...selectedDigitalId, status: "Revoked" };
      setSelectedDigitalId(updated);
      setDigitalIds((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Unable to revoke Digital ID.");
    }
  };

  const handleRenewDigitalId = async () => {
    if (!selectedDigitalId) {
      alert("Please select a Digital ID.");
      return;
    }

    try {
      const res = await axios.post(RENEW_DIGITAL_ID_URL(), {
        digitalId: selectedDigitalId.id,
        userId: user.id,
      });

      alert("Digital ID renewed successfully.");

      setSelectedDigitalId(res.data.identity);
      setDigitalIds((prev) =>
        prev.map((item) =>
          item.id === res.data.identity.id ? res.data.identity : item
        )
      );
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Unable to renew Digital ID.");
    }
  };

  const updateField = (name) => (e) => {
    const value = e.target.files ? e.target.files[0] : e.target.value;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  // =====================================
  // SOURCE CARD CLICK
  // All cards select the card. "Create a New Digital ID" skips
  // Device Details (panel hidden). All others show Device Details.
  // =====================================
  const handleSelectCard = (card) => {
    setSelectedKey(card.key);
  };

  // =====================================
  // NEXT
  // "create" → Choose Mode
  // All other cards → Advanced Options (skipping Choose Mode)
  // =====================================
  const handleNext = () => {
    const card = CARD_DEFS.find((c) => c.key === selectedKey);

    if (!card) {
      alert("Please select a verification method.");
      return;
    }

    const method = {
      title: card.title,
      description: card.description,
      ...(statusByTitle[card.title] || {}),
    };

    const baseState = {
      uploadedFiles,
      documentId: location.state?.documentId,
      selectedMethod: method,
      selectedDigitalId,
    };

    if (SKIP_DEVICE_DETAILS_KEYS.includes(card.key)) {
      navigate("/choosemode", { state: baseState });
    } else {
      navigate("/advanced", { state: { ...baseState, identity: method } });
    }
  };

  // =====================================
  // MOCK TEST CONNECTION (device panel)
  // =====================================
  const handleTestConnection = () => {
    setTestingConnection(true);
    setTimeout(() => {
      setTestingConnection(false);
      setConnectionOk(true);
    }, 900);
  };

  // =====================================
  // EMAIL OTP — SEND
  // =====================================
  const handleSendEmailOtp = async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(EMAIL_OTP_SEND_URL(), {
        userId: user?.id,
        email: fields.email,
      });
      setOtpSent(true);
    } catch (err) {
      console.error("Send OTP error:", err);
      setOtpError("Could not send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // =====================================
  // EMAIL OTP — VERIFY
  // =====================================
  const handleVerifyEmailOtp = async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(EMAIL_OTP_VERIFY_URL(), {
        userId: user?.id,
        otp: fields.otp,
      });
      setOtpVerified(true);
    } catch (err) {
      console.error("Verify OTP error:", err);
      setOtpError("Invalid or expired code. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // =====================================
  // ID VERIFICATION — VERIFY
  // =====================================
  const handleVerifyId = async () => {
    setIdError("");
    setIdLoading(true);
    try {
      const payload = new FormData();
      payload.append("userId", user?.id || "");
      payload.append("idNumber", fields.idNumber || "");
      if (fields.idFile) payload.append("idFile", fields.idFile);

      await axios.post(ID_VERIFICATION_URL(), payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIdVerified(true);
    } catch (err) {
      console.error("ID verification error:", err);
      setIdError("Verification failed. Please check the details and try again.");
    } finally {
      setIdLoading(false);
    }
  };

  const selectedCard = CARD_DEFS.find((c) => c.key === selectedKey);
  const selectedStatus = selectedCard ? statusByTitle[selectedCard.title] : null;

  return (
    <div className="identity-page">
      {/* STEP BAR */}
      <div className="step-bar">
        {steps.map((step, index) => {
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;

          let circleClass = "";
          let labelClass = "";
          if (isDone) {
            circleClass = "step-circle-done";
            labelClass = "step-label-done";
          } else if (isCurrent) {
            circleClass = "step-circle-active";
            labelClass = "step-label-active";
          }

          return (
            <div key={step} className="step-item">
              {index !== steps.length - 1 && (
                <div className={`step-line ${isDone ? "step-line-done" : ""}`} />
              )}
              <div className={`step-circle ${circleClass}`}>
                {isDone ? <Check size={12} /> : index + 1}
              </div>
              <p className={`step-label ${labelClass}`}>{step}</p>
            </div>
          );
        })}
      </div>

      {/* TWO COLUMN LAYOUT (collapses to one column when no panel is shown) */}
      <div className={`identity-grid ${(!selectedKey || SKIP_DEVICE_DETAILS_KEYS.includes(selectedKey)) ? "identity-grid-collapsed" : ""}`}>
        {/* ===== MAIN PANEL: SOURCE SELECTION ===== */}
        <div className="panel source-panel">
          <h2 className="panel-title">Select Digital Identity Source</h2>
          <p className="panel-subtitle">Choose how you want to access your digital identity for signing.</p>

          <div className="source-grid">
            {CARD_DEFS.map((card) => {
              const Icon = card.icon;
              const isSelected = selectedKey === card.key;
              const status = statusByTitle[card.title];
              return (
                <div
                  key={card.key}
                  className={`source-card ${isSelected ? "source-card-selected" : ""}`}
                  onClick={() => handleSelectCard(card)}
                >
                  <div className="source-card-top">
                    <span className={`radio-dot ${isSelected ? "radio-dot-active" : ""}`} />
                  </div>
                  <div className="source-card-icon">
                    <Icon size={20} />
                  </div>
                  <h3 className="source-card-title">{card.title}</h3>
                  <p className="source-card-desc">{card.description}</p>
                  {status?.verified && (
                    <span className="source-card-status">
                      <Check size={11} /> {status.statusText || "Verified"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pq-banner">
            <div className="pq-banner-icon"><ShieldCheck size={18} /></div>
            <div className="pq-banner-text">
              <p className="pq-banner-title">Post-Quantum Ready</p>
              <p className="pq-banner-desc">All identity sources support Post-Quantum (PQ) and Hybrid cryptographic algorithms.</p>
            </div>
            <a href="#" className="pq-banner-link">Learn More <ArrowRight size={13} /></a>
          </div>
        </div>

        {/* ===== SIDE PANEL: DEVICE DETAILS =====
             Hidden for "create" (goes straight to Choose Mode on Next).
             Shown for all other selected cards. */}
        {selectedKey && !SKIP_DEVICE_DETAILS_KEYS.includes(selectedKey) && (
          <div className="panel device-details-panel">
            <div className="panel-header-row">
              <h2 className="panel-title">Device Details</h2>
              {selectedKey === "device" && (
                <button className="btn-link" onClick={() => setConnectionOk(false)}>
                  <RefreshCw size={13} /> Refresh
                </button>
              )}
            </div>

            <div className="device-detail-body">
              {selectedKey === "device" && (
                <DeviceSignaturePanel
                  fields={fields}
                  updateField={updateField}
                  showPin={showPin}
                  setShowPin={setShowPin}
                  testingConnection={testingConnection}
                  connectionOk={connectionOk}
                  onTestConnection={handleTestConnection}
                />
              )}
              {selectedKey === "file" && (
                <DigitalIdFilePanel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "hsm" && (
                <HsmPanel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "cloud" && (
                <CloudSigningPanel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "windows" && <WindowsCertPanel />}
              {selectedKey === "pkcs11" && (
                <Pkcs11Panel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "mobile" && (
                <MobileSigningPanel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "csr" && (
                <CsrPanel fields={fields} updateField={updateField} />
              )}
              {selectedKey === "emailOtp" && (
                <EmailOtpPanel
                  fields={fields}
                  updateField={updateField}
                  onSend={handleSendEmailOtp}
                  onVerify={handleVerifyEmailOtp}
                  otpSent={otpSent}
                  loading={otpLoading}
                  error={otpError}
                  verified={otpVerified || selectedStatus?.verified}
                />
              )}
              {selectedKey === "idVerification" && (
                <IdVerificationPanel
                  fields={fields}
                  updateField={updateField}
                  onVerify={handleVerifyId}
                  loading={idLoading}
                  error={idError}
                  verified={idVerified || selectedStatus?.verified}
                />
              )}
              {selectedKey === "existing" && (
                <ExistingDigitalIdPanel
                  digitalIds={digitalIds}
                  loading={loadingDigitalIds}
                  selectedDigitalId={selectedDigitalId}
                  setSelectedDigitalId={setSelectedDigitalId}
                  onRevoke={handleRevokeDigitalId}
                  onRenew={handleRenewDigitalId}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* BACK / NEXT */}
      <div className="footer-actions">
        <button onClick={() => navigate("/app")} className="btn-secondary">
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={handleNext} className="btn-primary">
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// =====================================================
// 1. SIGNATURE CREATION DEVICE PANEL
// =====================================================
function DeviceSignaturePanel({
  fields, updateField, showPin, setShowPin,
  testingConnection, connectionOk, onTestConnection,
}) {
  return (
    <>
      <div className="status-banner status-banner-success">
        <Check size={13} /> Token Connected Successfully
      </div>

      <div className="device-info-row">
        <div className="device-info-icon">
          <Smartphone size={22} />
        </div>
        <div className="device-info-grid">
          <p className="device-info-name">YubiKey 5 NFC</p>
          <div><span className="field-label">Serial Number</span><p>5.216.12456</p></div>
          <div><span className="field-label">Firmware</span><p>5.7.3</p></div>
          <div><span className="field-label">Manufacturer</span><p>Yubico</p></div>
        </div>
      </div>

      <div className="field-group">
        <div className="field-row-between">
          <label className="field-label">Slot / Certificate</label>
          <a href="#" className="link-small">View Certificate</a>
        </div>
        <div className="field-select">
          PIV Slot 9a (Digital Signature)
          <ChevronDown size={14} />
        </div>
      </div>

      <div className="field-group">
        <label className="field-label">PIN / Passcode</label>
        <div className="field-input-with-icon">
          <input
            type={showPin ? "text" : "password"}
            value={fields.pin ?? "••••••••"}
            onChange={updateField("pin")}
          />
          <button type="button" onClick={() => setShowPin((s) => !s)}>
            {showPin ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>

      <label className="checkbox-row">
        <input type="checkbox" defaultChecked /> Remember PIN
      </label>

      <button className="btn-block-outline" onClick={onTestConnection}>
        {testingConnection ? "Testing..." : "Test Connection"}
      </button>

      <div className="selected-cert-section">
        <h4 className="section-subtitle">Selected Certificate</h4>
        <div className="cert-grid">
          <span className="cert-label">Common Name (CN)</span><span>Demo Admin</span>
          <span className="cert-label">Issuer</span><span>Intrust Quantum CA (PQ)</span>
          <span className="cert-label">Valid From</span><span>May 19, 2024</span>
          <span className="cert-label">Valid Till</span><span>May 16, 2028</span>
          <span className="cert-label">Key Algorithm</span>
          <span><span className="badge-pq">Dilithium3 (Post-Quantum)</span></span>
          <span className="cert-label">Signature Algorithm</span><span>Dilithium3</span>
          <span className="cert-label">Key Usage</span><span>Digital Signature, Non-Repudiation</span>
        </div>
      </div>

      {connectionOk && (
        <div className="status-banner status-banner-success">
          <Check size={13} /> Certificate is valid and ready to sign
        </div>
      )}
    </>
  );
}

// =====================================================
// 2. DIGITAL ID FROM FILE
// =====================================================
function DigitalIdFilePanel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">Upload Digital ID</label>
        <label className="file-drop">
          {fields.idFileName || "Click to select .p12 / .pfx / .pem / .cer file"}
          <input type="file" hidden onChange={(e) => {
            updateField("idFileName")({ target: { value: e.target.files?.[0]?.name || "" } });
          }} />
        </label>
      </div>

      <div className="field-group">
        <label className="field-label">Password</label>
        <input type="password" placeholder="Enter file password" value={fields.filePassword || ""} onChange={updateField("filePassword")} className="field-input" />
      </div>

      <button className="btn-block-primary">Validate Certificate</button>
    </>
  );
}

// =====================================================
// 3. HSM
// =====================================================
function HsmPanel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">HSM Name</label>
        <input className="field-input" placeholder="e.g. Luna Network HSM" value={fields.hsmName || ""} onChange={updateField("hsmName")} />
      </div>
      <div className="field-group">
        <label className="field-label">Slot</label>
        <input className="field-input" placeholder="Slot ID" value={fields.slot || ""} onChange={updateField("slot")} />
      </div>
      <div className="field-group">
        <label className="field-label">Certificate</label>
        <div className="field-select">Select a certificate <ChevronDown size={14} /></div>
      </div>
      <button className="btn-block-primary">Connect</button>
    </>
  );
}

// =====================================================
// 4. CLOUD SIGNING SERVICE
// =====================================================
function CloudSigningPanel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">Cloud Provider</label>
        <select className="field-select-native" value={fields.cloudProvider || "digicert"} onChange={updateField("cloudProvider")}>
          <option value="digicert">DigiCert</option>
          <option value="entrust">Entrust</option>
          <option value="azure">Azure Key Vault</option>
          <option value="aws">AWS KMS</option>
        </select>
      </div>
      <div className="field-group">
        <label className="field-label">Certificate</label>
        <div className="field-select">Select a certificate <ChevronDown size={14} /></div>
      </div>
      <button className="btn-block-primary">Connect</button>
    </>
  );
}

// =====================================================
// 5. WINDOWS CERTIFICATE STORE
// =====================================================
function WindowsCertPanel() {
  const mockCerts = [
    { name: "Demo Admin — Personal Store", expiry: "May 16, 2028" },
    { name: "Company Root CA", expiry: "Jan 4, 2030" },
  ];
  return (
    <>
      <p className="section-subtitle">Available Certificates</p>
      <div className="cert-list">
        {mockCerts.map((cert, i) => (
          <label className="cert-list-item" key={cert.name}>
            <input type="radio" name="winCert" defaultChecked={i === 0} />
            <span>
              <span className="cert-list-name">{cert.name}</span>
              <span className="cert-list-expiry">Valid till {cert.expiry}</span>
            </span>
          </label>
        ))}
      </div>
      <button className="btn-block-primary">Use Selected Certificate</button>
    </>
  );
}

// =====================================================
// 6. PKCS#11 TOKEN
// =====================================================
function Pkcs11Panel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">Library Path</label>
        <input className="field-input" placeholder="/usr/lib/pkcs11/library.so" value={fields.libPath || ""} onChange={updateField("libPath")} />
      </div>
      <div className="field-group">
        <label className="field-label">Token Slot</label>
        <input className="field-input" placeholder="Slot number" value={fields.tokenSlot || ""} onChange={updateField("tokenSlot")} />
      </div>
      <div className="field-group">
        <label className="field-label">PIN</label>
        <input type="password" className="field-input" placeholder="Token PIN" value={fields.pkPin || ""} onChange={updateField("pkPin")} />
      </div>
      <button className="btn-block-outline">Test Connection</button>
    </>
  );
}

// =====================================================
// 7. MOBILE / REMOTE SIGNING
// =====================================================
function MobileSigningPanel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">Phone Number</label>
        <input className="field-input" placeholder="+91 98765 43210" value={fields.phone || ""} onChange={updateField("phone")} />
      </div>
      <button className="btn-block-primary">Connect Mobile</button>
    </>
  );
}

// =====================================================
// 8. CSR / GET CERTIFICATE
// =====================================================
function CsrPanel({ fields, updateField }) {
  return (
    <>
      <div className="field-group">
        <label className="field-label">Common Name</label>
        <input className="field-input" placeholder="e.g. Demo Admin" value={fields.commonName || ""} onChange={updateField("commonName")} />
      </div>
      <div className="field-group">
        <label className="field-label">Organization</label>
        <input className="field-input" placeholder="Organization name" value={fields.csrOrg || ""} onChange={updateField("csrOrg")} />
      </div>
      <div className="field-group">
        <label className="field-label">Country</label>
        <input className="field-input" placeholder="e.g. IN" value={fields.country || ""} onChange={updateField("country")} />
      </div>
      <button className="btn-block-primary">Generate CSR</button>
    </>
  );
}

// =====================================================
// 9. EXISTING DIGITAL ID
// =====================================================
function ExistingDigitalIdPanel({
  digitalIds,
  loading,
  selectedDigitalId,
  setSelectedDigitalId,
  onRevoke,
  onRenew,
}) {
  if (loading) {
    return <p>Loading Digital IDs...</p>;
  }

  if (!digitalIds || digitalIds.length === 0) {
    return (
      <div className="status-banner status-banner-error">
        No Digital IDs found.
      </div>
    );
  }

  return (
    <>
      <p className="section-subtitle">Select an Existing Digital Identity</p>

      <div className="cert-list">
        {digitalIds.map((item) => (
          <label key={item.id} className="cert-list-item">
            <input
              type="radio"
              name="existingDigitalId"
              checked={selectedDigitalId?.id === item.id}
              onChange={() => setSelectedDigitalId(item)}
            />
            <span>
              <span className="cert-list-name">{item.identity_name}</span>
              <span className="cert-list-expiry">{item.algorithm}</span>
              <span className="cert-list-expiry">Status : {item.status}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="existing-id-actions">
        <button
          className="btn-block-outline"
          onClick={onRenew}
          disabled={!selectedDigitalId}
        >
          Renew Digital ID
        </button>

        <button
          className="btn-block-danger"
          onClick={onRevoke}
          disabled={!selectedDigitalId || selectedDigitalId?.status === "Revoked"}
        >
          Revoke Digital ID
        </button>
      </div>
    </>
  );
}

// =====================================================
// 10. EMAIL OTP — connected to existing backend
// =====================================================
function EmailOtpPanel({ fields, updateField, onSend, onVerify, otpSent, loading, error, verified }) {
  let sendLabel = "Send OTP";
  if (loading && !otpSent) {
    sendLabel = "Sending...";
  } else if (otpSent) {
    sendLabel = "Resend OTP";
  }

  return (
    <>
      <div className="device-info-row">
        <div className="device-info-icon"><Mail size={20} /></div>
        <div>
          <p className="device-info-name">Email Verification</p>
          <p className="field-label">One-time code sent to your registered email</p>
        </div>
      </div>

      <div className="field-group">
        <label className="field-label">Email Address</label>
        <input className="field-input" placeholder="you@company.com" value={fields.email || ""} onChange={updateField("email")} disabled={otpSent} />
      </div>

      {!verified && (
        <button className="btn-block-outline" onClick={onSend} disabled={loading}>
          {sendLabel}
        </button>
      )}

      {otpSent && !verified && (
        <>
          <div className="field-group">
            <label className="field-label">Enter OTP</label>
            <input className="field-input otp-input" maxLength={6} placeholder="••••••" value={fields.otp || ""} onChange={updateField("otp")} />
          </div>
          <button className="btn-block-primary" onClick={onVerify} disabled={loading || !fields.otp}>
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </>
      )}

      {error && <div className="status-banner status-banner-error">{error}</div>}

      {verified && (
        <div className="status-banner status-banner-success">
          <Check size={13} /> Email verified successfully
        </div>
      )}
    </>
  );
}

// =====================================================
// 11. ID VERIFICATION — connected to existing backend
// =====================================================
function IdVerificationPanel({ fields, updateField, onVerify, loading, error, verified }) {
  return (
    <>
      <div className="device-info-row">
        <div className="device-info-icon"><IdCard size={20} /></div>
        <div>
          <p className="device-info-name">Government ID Verification</p>
          <p className="field-label">Passport, National ID, or Driving License</p>
        </div>
      </div>

      <div className="field-group">
        <label className="field-label">Upload ID</label>
        <label className="file-drop">
          {fields.idFile?.name || "Click to upload a photo or scan of your ID"}
          <input type="file" hidden onChange={updateField("idFile")} />
        </label>
      </div>

      <div className="field-group">
        <label className="field-label">ID Number</label>
        <input className="field-input" placeholder="Document ID number" value={fields.idNumber || ""} onChange={updateField("idNumber")} disabled={verified} />
      </div>

      {!verified && (
        <button className="btn-block-primary" onClick={onVerify} disabled={loading || !fields.idNumber}>
          {loading ? "Verifying..." : "Verify"}
        </button>
      )}

      {error && <div className="status-banner status-banner-error">{error}</div>}

      {verified && (
        <div className="status-banner status-banner-success">
          <Check size={13} /> Identity document verified
        </div>
      )}
    </>
  );
}